import { useState, useEffect } from "react";
import {
  useCreateDiscount,
  useUpdateDiscount,
} from "http-hooks/discount/useDiscount";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Modal from "components/ui/modals";

const typeOptions = [
  { value: "A", label: "Annual" },
  { value: "B", label: "Before-tax Bill" },
  { value: "C", label: "After-tax Bill" },
];

const categoryOptions = [
  { value: "P", label: "Purchase" },
  { value: "S", label: "Sales" },
];

const additionOptions = [
  { value: 0, label: "Less (Discount)" },
  { value: 1, label: "Add (Charge)" },
];

const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Non-Active" },
];

export default function SetDiscount({ discount, onClose, open }) {
  const [discountDetails, setDiscountDetails] = useState({
    code: "",
    name: "",
    shortname: "",
    tp: "",
    codeid: "",
    type: "",
    is_addition: 0,
    ag_status: 1,
  });

  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateDiscount();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateDiscount();

  const isPending = isCreating || isUpdating;

  useEffect(() => {
    if (discount) {
      setDiscountDetails({
        code: discount.code || "",
        name: discount.name || "",
        shortname: discount.shortname || "",
        tp: discount.tp || "",
        codeid: discount.codeid || "",
        type: discount.type || "",
        is_addition: discount.is_addition ?? 0,
        ag_status: discount.ag_status ?? 1,
      });
    } else {
      setDiscountDetails({
        code: "",
        name: "",
        shortname: "",
        tp: "",
        codeid: "",
        type: "",
        is_addition: 0,
        ag_status: 1,
      });
    }
  }, [discount]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setDiscountDetails((prevData) => ({
      ...prevData,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));
  };

  const handleSelectChange = (field, option) => {
    setDiscountDetails((prevData) => ({
      ...prevData,
      [field]: option?.value ?? "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (discount) {
        await mutateUpdate({ id: discount.id, data: discountDetails });
      } else {
        await mutateCreate(discountDetails);
      }
      onClose();
    } catch (error) {
      console.error("Submit failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {discount ? "Update" : "Add"} Discount
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold leading-6 text-gray-900 hover:text-gray-700"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-discount-form"
            disabled={isPending}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending
              ? discount
                ? "Updating..."
                : "Creating..."
              : discount
              ? "Update Discount"
              : "Create Discount"}
          </button>
        </div>
      }
    >
      <form id="set-discount-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            type="number"
            name="code"
            label="Order"
            placeholder="1-10"
            value={discountDetails.code}
            onChange={handleInputChange}
            isRequired={true}
            min="1"
          />
          <Input
            type="text"
            name="name"
            label="Name"
            value={discountDetails.name}
            onChange={handleInputChange}
            maxLength={30}
            isRequired={true}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            type="text"
            name="shortname"
            label="Short Name"
            value={discountDetails.shortname}
            onChange={handleInputChange}
            maxLength={10}
            isRequired={true}
          />
          <Input
            type="text"
            name="codeid"
            label="Code ID"
            value={discountDetails.codeid}
            onChange={handleInputChange}
            maxLength={6}
            isRequired={true}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            value={typeOptions.find((option) => option.value === discountDetails.tp)}
            onChange={(option) => handleSelectChange("tp", option)}
            options={typeOptions}
            label="Type"
            placeholder="Select type"
            isRequired={true}
            isSearchable={false}
          />
          <Select
            value={categoryOptions.find((option) => option.value === discountDetails.type)}
            onChange={(option) => handleSelectChange("type", option)}
            options={categoryOptions}
            label="Category"
            placeholder="Select category"
            isRequired={true}
            isSearchable={false}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            value={additionOptions.find((option) => option.value === discountDetails.is_addition)}
            onChange={(option) => handleSelectChange("is_addition", option)}
            options={additionOptions}
            label="Plus / Minus"
            placeholder="Select"
            isRequired={true}
            isSearchable={false}
          />
          <Select
            value={statusOptions.find((option) => option.value === discountDetails.ag_status)}
            onChange={(option) => handleSelectChange("ag_status", option)}
            options={statusOptions}
            label="Status"
            placeholder="Select status"
            isRequired={true}
            isSearchable={false}
          />
        </div>
      </form>
    </Modal>
  );
}
